import { format, parseISO, formatISO } from 'date-fns';
import { zonedTimeToUtc, utcToZonedTime } from 'date-fns-tz';

// IST timezone
const TIME_ZONE = 'Asia/Kolkata';

/**
 * Convert a date to IST timezone
 * @param {Date|string} date - The date to convert
 * @returns {Date} The date in IST timezone
 */
export const toIST = (date) => {
  const parsedDate = typeof date === 'string' ? parseISO(date) : date;
  return utcToZonedTime(parsedDate, TIME_ZONE);
};

/**
 * Convert a date from IST to UTC
 * @param {Date|string} date - The date in IST to convert
 * @returns {Date} The date in UTC
 */
export const fromIST = (date) => {
  const parsedDate = typeof date === 'string' ? parseISO(date) : date;
  return zonedTimeToUtc(parsedDate, TIME_ZONE);
};

/**
 * Format a date in IST timezone
 * @param {Date|string} date - The date to format
 * @param {string} formatStr - The format string (date-fns format)
 * @returns {string} The formatted date string
 */
export const formatInIST = (date, formatStr = 'yyyy-MM-dd hh:mm a') => {
  const istDate = toIST(date);
  return format(istDate, formatStr);
};

/**
 * Parse a time string (e.g., "10:00 AM") and combine with a date in IST timezone
 * @param {Date|string} date - The date to combine with
 * @param {string} timeStr - The time string (e.g., "10:00 AM")
 * @returns {Date} The combined date and time in IST timezone
 */
export const combineDateTime = (date, timeStr) => {
  const parsedDate = typeof date === 'string' ? parseISO(date) : date;
  const [time, meridiem] = timeStr.split(' ');
  const [hours, minutes] = time.split(':');
  
  let hour = parseInt(hours, 10);
  if (meridiem === 'PM' && hour !== 12) hour += 12;
  if (meridiem === 'AM' && hour === 12) hour = 0;
  
  const istDate = toIST(parsedDate);
  istDate.setHours(hour);
  istDate.setMinutes(parseInt(minutes, 10));
  
  return istDate;
};

/**
 * Get current time in IST
 * @returns {Date} Current time in IST timezone
 */
export const getCurrentTimeIST = () => {
  return toIST(new Date());
};

/**
 * Check if a date is in the future (in IST timezone)
 * @param {Date|string} date - The date to check
 * @returns {boolean} True if the date is in the future
 */
export const isFutureIST = (date) => {
  const istDate = toIST(date);
  const currentIST = getCurrentTimeIST();
  return istDate > currentIST;
};

/**
 * Get weekend dates (Saturday and Sunday) for test booking
 * @returns {Array} Array of date objects for the available weekend dates
 */
export const getWeekendDates = () => {
  const today = new Date();
  const currentDay = today.getDay(); // 0 is Sunday, 6 is Saturday
  const currentHour = today.getHours();
  
  const dates = [];
  
  // Calculate days until next Saturday
  let daysToSaturday = (6 - currentDay) % 7; // 6 is Saturday
  if (currentDay === 6) { // If today is Saturday
    daysToSaturday = 0;
  }
  
  // Get the date for the upcoming Saturday (or today if it's Saturday)
  const saturdayDate = new Date(today);
  saturdayDate.setDate(today.getDate() + daysToSaturday);
  
  // Get the date for the upcoming Sunday (or tomorrow if today is Saturday)
  const sundayDate = new Date(saturdayDate);
  sundayDate.setDate(saturdayDate.getDate() + 1);
  
  // If today is Saturday and it's before 6 PM, include today
  if (currentDay === 6 && currentHour < 18) {
    dates.push(saturdayDate);
  } 
  // If today is Saturday but after 6 PM, only include Sunday
  else if (currentDay === 6 && currentHour >= 18) {
    dates.push(sundayDate);
  }
  // If today is Sunday and it's before 6 PM, include today
  else if (currentDay === 0 && currentHour < 18) {
    dates.push(today);
    
    // Also include next weekend
    const nextSaturday = new Date(today);
    nextSaturday.setDate(today.getDate() + 6);
    const nextSunday = new Date(nextSaturday);
    nextSunday.setDate(nextSaturday.getDate() + 1);
    
    dates.push(nextSaturday, nextSunday);
  } 
  // If today is Sunday but after 6 PM, include next weekend
  else if (currentDay === 0 && currentHour >= 18) {
    const nextSaturday = new Date(today);
    nextSaturday.setDate(today.getDate() + 6);
    const nextSunday = new Date(nextSaturday);
    nextSunday.setDate(nextSaturday.getDate() + 1);
    
    dates.push(nextSaturday, nextSunday);
  }
  // For weekdays, include the upcoming weekend
  else {
    dates.push(saturdayDate, sundayDate);
  }
  
  return dates;
};

// get current date and next 3 dates
export const getDates = () => {
  const dates = [];
  const today = new Date();
  dates.push(today);
  for (let i = 1; i < 4; i++) {
    const date = new Date(today);
    date.setDate(today.getDate() + i);
    dates.push(date);
  }
  return dates;
};
  
